<template>
  <aside>
    <social-login />
    <Form ref="loginForm" class="login-form" method="post" @submit="login">
      <div class="divider">
        <hr />
        <span>{{ $t('or') }}</span>
        <hr />
      </div>
      <base-input
        v-model="user.email"
        type="email"
        name="email"
        autocomplete="email"
        spellcheck="false"
        autocapitalize="off"
        autocorrect="off"
        required="true"
        :validation-rules="emailValidationSchema"
        :validation-errors="formError ? [formError] : validationErrors"
        :hide-individual-errors="formError.length > 0"
        @update:model-value="updateEmail($event)"
      >
        <template #label>
          {{ $t('Email') }}
        </template>
        <template #errorMessage="{ failedRules }">
          <!-- Show server validation errors first -->
          <div v-if="validationErrors.length > 0">
            {{ validationErrors[0] }}
          </div>
          <!-- Fall back to client-side validation -->
          <translate v-else-if="failedRules === 'invalid_email'" tag="div">
            Please enter a valid email address
          </translate>
          <translate v-else-if="failedRules === 'required'" tag="div">
            Please enter your email address
          </translate>
          <template v-else>
            {{ failedRules }}
          </template>
        </template>
      </base-input>
      <base-password
        v-model="user.password"
        name="password"
        required="true"
        :validation-rules="passwordValidationSchema"
        :validation-errors="formError ? [formError] : validationErrors"
        :hide-individual-errors="formError.length > 0"
        @update:model-value="updatePassword($event)"
      >
        <template #label>
          {{ $t('Password') }}
        </template>
        <template #errorMessage="{ failedRules }">
          <translate v-if="failedRules.includes('required')" tag="div">
            Please enter your password
          </translate>
        </template>
      </base-password>

      <!-- Shared validation error message displayed below both fields -->
      <div v-if="formError.length" class="input-error-msg">
        {{ formError }}
      </div>

      <button class="asblock primary login-btn">
        {{ $t('Log in') }}
      </button>
      <pu-modal
        class="reset-password"
        :use-portal="useModalPortal"
        :modal-class="'reset-password-modal'"
        modal-hash="reset-password"
      >
        <template #modal-trigger>
          <a class="pw-link" @click="scrolltop">{{ $t('Forgot password') }} </a>
        </template>
        <reset-password
          :user-email="user.email || email"
          @update-email="user.email = $event"
        />
      </pu-modal>
    </Form>
    <nuxt-to-meta :nuxt2meta="head" />
  </aside>
</template>

<script lang="ts">
import { Field, Form } from 'vee-validate'
import * as z from 'zod'
import AuthMixin from '~/mixins/AuthMixin'
import { AuthMachineInterpreter } from '~/apps/auth/authMachine'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

@Component({
  components: { Form, Field },
  mixins: [AuthMixin],
})
class LoginForm extends Vue {
  @Prop()
  email?: string

  @Prop({ default: false })
  useModalPortal: boolean

  declare authState: AuthMachineInterpreter['state'] | null
  declare getAuthMachine: () => AuthMachineInterpreter

  get head() {
    return {
      bodyAttrs: {
        class: this.authState?.matches('loggingIn') ? 'pagemask' : '',
      },
    }
  }

  user = {
    email: '',
    password: '',
  }

  validationErrors: string[] = []
  formError: string = ''

  declare $refs: {
    loginForm: InstanceType<typeof Form>
  }

  emailValidationSchema = z
    .string({ required_error: 'required' })
    .min(1, { message: 'required' })
    .email({ message: 'invalid_email' })

  passwordValidationSchema = z
    .string({ required_error: 'required' })
    .min(1, { message: 'required' })

  authMounted() {
    this.user.email = this.email || ''
    this.getAuthMachine().onTransition((state) => {
      if (state.matches('loggedOut.error')) {
        const error = state.context.error
        if (Array.isArray(error)) {
          this.validationErrors = error
          this.formError = ''
        } else if (error) {
          if (error.formError) {
            this.formError = error.formError
            this.validationErrors = []
          } else {
            this.validationErrors = error.message ? [error.message] : []
            this.formError = ''
          }
        }
      }
      if (state.matches('loginSuccess')) {
        this.$emit('loginSuccess')
      }
    })
    if (this.user.email) {
      this.updateEmail(this.user.email)
    }
  }

  clearErrors() {
    void this.$nextTick(() => {
      this.validationErrors = []
      this.formError = ''
    })
  }

  updateEmail(value: string) {
    this.clearErrors()
    this.getAuthMachine().send('CHANGE_EMAIL', { value })
  }

  updatePassword(value: string) {
    this.clearErrors()
    this.getAuthMachine().send('CHANGE_PASSWORD', { value })
  }

  scrolltop() {
    const useraccount = document.querySelector('#user') as HTMLElement
    if (useraccount) {
      useraccount.scrollTop = 0
    }
  }

  login() {
    this.getAuthMachine().send('LOG_IN_EMAIL', { langCode: this.$i18n.locale })
  }
}

export default toNative(LoginForm)
</script>
<style>
/* reset pw modal when on booking form evoked from login modal */
.reset-password-modal .modal-body {
  padding: 1em;
}

.reset-password-modal.modal-container {
  min-height: calc(100vh - 6vw);
}

.reset-password-modal.desktop.modal-container {
  min-height: max-content;
}

.reset-password #reset-password {
  padding: 0 0 1em 0;
}
</style>
<style scoped>
form {
  padding: 1.5em;
}

.divider {
  margin: 0;
}

.login-form :deep(label) {
  margin-top: 0.75em;
  display: block;
}

.pw-link {
  font-size: var(--body);
}

.login-btn {
  margin: 1em 0;
}

.reset-password :deep(.modal-container) {
  width: 79vw;
  min-width: 12em;
  margin: auto;
  left: auto;
}

.reset-password :deep(.modal-body) {
  padding: 1em;
}
</style>
